import { ArrowUpRight, Check } from "lucide-react";
import SubHeader from "@/components/SubHeader";
import { Reveal, Chapter } from "@/components/Reveal";

const BROCHURES = {
  india: {
    name: "India",
    flag: "🇮🇳",
    currency: "INR (₹)",
    headline: "The India Edition",
    intro:
      "From Rajasthan royal circuits to Kerala backwaters, Indian DMCs run some of the world's most layered itineraries. Travel-Tek gives your team a branded website, package management, inquiry capture and quotations — in ₹, and in the currencies your international clients think in.",
    img: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    imgAlt: "Taj Mahal, India",
    highlights: [
      "Quotations in ₹ with multi-currency display for international clients",
      "Domestic, inbound and experiential package workflows",
      "WhatsApp-ready inquiry handling as an optional integration",
      "Managed cloud hosting with fast delivery across India",
    ],
  },
  uae: {
    name: "UAE",
    flag: "🇦🇪",
    currency: "AED",
    headline: "The UAE Edition",
    intro:
      "Luxury FITs, MICE groups and stopover programs move fast in the Emirates. Travel-Tek gives UAE destination businesses a premium white-label presence and an inquiry-to-quotation workflow that matches the market's pace — in AED and beyond.",
    img: "https://images.unsplash.com/photo-1518684079-3c830dcef090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxkdWJhaSUyMHVhZSUyMHNreWxpbmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkwMDc0OTU2fDA&ixlib=rb-4.1.0&q=85",
    imgAlt: "Burj Al Arab, Dubai",
    highlights: [
      "Quotations in AED with USD and EUR display for global clients",
      "Workflows shaped for luxury FIT, MICE and stopover business",
      "Premium media library to present a high-end brand",
      "Fully managed infrastructure — no in-house tech team needed",
    ],
  },
  "southeast-asia": {
    name: "Southeast Asia",
    flag: "🌏",
    currency: "THB · MYR · SGD · IDR · USD",
    headline: "The Southeast Asia Edition",
    intro:
      "Island hopping in Thailand, heritage trails in Vietnam, resorts in Bali — Southeast Asian DMCs often operate across borders. Travel-Tek supports multi-market, multi-currency destination businesses from a single white-label platform.",
    img: "https://images.unsplash.com/photo-1720655260134-90960a710cc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzb3V0aGVhc3QlMjBhc2lhJTIwdHJvcGljYWwlMjB0ZW1wbGV8ZW58MHx8fHwxNzkwMDc0OTU2fDA&ixlib=rb-4.1.0&q=85",
    imgAlt: "Southeast Asian temple heritage",
    highlights: [
      "Local-currency quoting per market — THB, MYR, SGD, IDR, USD",
      "One platform per country brand, or multi-market operations",
      "Islands, heritage, adventure and wellness package workflows",
      "Regional cloud delivery for fast sites across the region",
    ],
  },
};

const PLANS = [
  { name: "Launch", tag: "For emerging DMCs.", features: ["Branded website on your domain", "Destinations & packages", "Inquiry management", "Quotation system"], featured: false },
  { name: "Grow", tag: "For established DMCs.", features: ["Everything in Launch", "Hotels, activities & transfers", "Multi-currency quoting", "Analytics & media library"], featured: true },
  { name: "Enterprise", tag: "For larger destination businesses.", features: ["Everything in Grow", "Multi-market operations", "Custom integrations", "Priority support & SLAs"], featured: false },
];

export default function Brochure({ market }) {
  const b = BROCHURES[market];
  if (!b) {
    return (
      <div className="min-h-screen bg-paper">
        <SubHeader cta={false} />
        <div className="container-x py-32 text-center">
          <h1 className="font-serif text-4xl text-ink">Brochure not found.</h1>
          <div className="mt-6 flex justify-center gap-4">
            {Object.keys(BROCHURES).map((k) => (
              <a key={k} href={`/brochures/${k}`} className="text-terra underline underline-offset-4" data-testid={`brochure-fallback-link-${k}`}>
                {BROCHURES[k].name}
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={`brochure-page-${market}`}>
      <SubHeader />
      <main>
        <section className="container-x grid items-center gap-12 pb-20 pt-16 lg:grid-cols-12 lg:pt-24">
          <div className="lg:col-span-6">
            <Reveal><Chapter index={1} label={`Country Brochure · ${b.name}`} /></Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-8 font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid={`brochure-headline-${market}`}>
                {b.headline}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-stone2 sm:text-lg">{b.intro}</p>
              <div className="mt-6 inline-flex items-center gap-3 rounded-full border border-sand bg-white px-4 py-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-stone2">Native currency</span>
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-terra">{b.currency}</span>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href="/#demo"
                  data-testid={`brochure-demo-cta-${market}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra"
                >
                  Get {b.name} Pricing
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/brochures/${market}/pdf`}
                  download
                  data-testid={`brochure-pdf-download-${market}`}
                  className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
                >
                  <svg className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  Download PDF One-Pager
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="overflow-hidden rounded-[1.75rem] border border-sand shadow-[0_40px_80px_-35px_rgba(22,22,22,0.4)]">
              <img src={b.img} alt={b.imgAlt} className="h-[320px] w-full object-cover sm:h-[440px]" data-testid={`brochure-image-${market}`} />
            </div>
          </Reveal>
        </section>

        <section className="border-y border-sand bg-parch py-20">
          <div className="container-x grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <Chapter index={2} label="What's Included" />
              <h2 className="mt-6 font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl">
                What your {b.name} DMC gets
              </h2>
            </Reveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
              {b.highlights.map((h, i) => (
                <Reveal key={h} delay={0.07 * i}>
                  <div className="flex h-full items-start gap-3 rounded-2xl border border-sand bg-white p-6" data-testid={`brochure-highlight-${market}-${i + 1}`}>
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-terra" />
                    <p className="text-sm font-medium leading-relaxed text-ink">{h}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-24">
          <div className="container-x">
            <Reveal><Chapter index={3} label="Plans" /></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl">
                {b.name} pricing, built around your business
              </h2>
            </Reveal>
            <div className="mt-12 grid gap-5 lg:grid-cols-3">
              {PLANS.map((p, i) => (
                <Reveal key={p.name} delay={0.09 * i}>
                  <div
                    data-testid={`brochure-plan-${market}-${p.name.toLowerCase()}`}
                    className={`flex h-full flex-col rounded-2xl border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                      p.featured ? "border-ink bg-ink text-paper" : "border-sand bg-white hover:border-ink/30"
                    }`}
                  >
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">{p.name}</p>
                    <p className={`mt-3 font-serif text-2xl italic ${p.featured ? "text-paper" : "text-ink"}`}>{p.tag}</p>
                    <ul className="mt-7 flex-1 space-y-3">
                      {p.features.map((f) => (
                        <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-paper/75" : "text-stone2"}`}>
                          <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-terra" : "text-emerald2"}`} />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <a
                      href="/#demo"
                      data-testid={`brochure-plan-cta-${market}-${p.name.toLowerCase()}`}
                      className={`group mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300 ${
                        p.featured ? "bg-terra text-paper hover:bg-paper hover:text-ink" : "border border-ink/20 text-ink hover:bg-ink hover:text-paper"
                      }`}
                    >
                      Get a Custom Quote
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-8 text-center text-sm text-stone2" data-testid={`brochure-disclaimer-${market}`}>
                Exact {b.name} pricing is shared on a short call — it varies by business requirements and integrations.
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <footer className="border-t border-sand bg-ink">
        <div className="container-x flex flex-wrap items-center justify-between gap-4 py-8">
          <p className="font-serif text-xl text-paper">Travel-Tek<span className="text-terra">.</span></p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40">Powering Destination Experts · travel-tek.com</p>
          <div className="flex gap-5">
            {Object.keys(BROCHURES).filter((k) => k !== market).map((k) => (
              <a key={k} href={`/brochures/${k}`} data-testid={`brochure-crosslink-${k}`} className="text-xs text-paper/60 underline decoration-terra/50 underline-offset-4 hover:text-paper">
                {BROCHURES[k].flag} {BROCHURES[k].name} brochure
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
